/**
 * Connection Submission Utilities
 * Pure functions for handling connection form submission logic
 */

import type { Dispatch } from '@reduxjs/toolkit';

import { MESSAGE_SUBTYPES, MESSAGE_TYPES } from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import { addMessage } from '../data-store/copilotSlice';
import { showError } from '../services/messageService';

import { hasUserProvidedPrompts } from './promptDetectionUtils';

/**
 * Show connection helper prompt with 3 option buttons
 */
export const showConnectionHelperPrompt = (
  dispatch: Dispatch,
  getState: () => unknown
): void => {
  const state = getState() as { copilot?: CopilotState };
  const hasPrompts = hasUserProvidedPrompts('Connection', state.copilot!);

  if (hasPrompts) {
    const providedPrompt = state.copilot?.connectionPrompt || '';

    dispatch(
      addMessage({
        id: `connection-prompt-instructions-${Date.now()}`,
        type: MESSAGE_TYPES.AI_RESPONSE,
        subType: 'promptInstructions',
        timestamp: Date.now(),
        text: `Hey, it seems you've already provided the additional information.
${providedPrompt}
Do you want to continue with above instruction?`,
        buttons: [
          { label: 'Yes', action: 'continue_with_instructions' },
          { label: 'No', action: 'modify_instructions' },
        ],
      })
    );
  } else {
    dispatch(
      addMessage({
        id: `connection-helper-${Date.now()}`,
        type: MESSAGE_TYPES.AI_RESPONSE,
        subType: MESSAGE_SUBTYPES.THREE_BUTTONS_HELPER_TEXT,
        timestamp: Date.now(),
        buttons: [
          { label: 'Specify Prompt', action: 'connection_help_yes' },
          {
            label: 'Upload Browser Recording',
            action: 'connection_help_yes',
          },
          { label: 'Skip for Now', action: 'connection_help_no' },
        ],
      })
    );
  }
};

/**
 * Handle connection form submission
 */
export const handleConnectionSubmit = (
  dispatch: Dispatch,
  formData: Record<string, unknown>,
  isValid: boolean,
  getState: () => unknown
): void => {
  if (!isValid) {
    showError(dispatch, 'Please check connection details');

    return;
  }

  // Don't mark as completed here - wait for API success
  showConnectionHelperPrompt(dispatch, getState);
};
